import { PDFDocument, PDFPage, PDFFont, RGB, rgb } from 'pdf-lib';
import type { NormalizedAttachment, NormalizedEmail } from './parseEml';
import { resolveCidInHtml } from './resolveCidInHtml';
import { sanitizeEmailHtml } from './sanitizeEmailHtml';
import { embedBodyFonts, embedStandardFallbackFonts, type EmbeddedBodyFonts } from './pdfFonts';
import type { ImageFitMode, PdfExportQuality } from './pdfExportQuality';
import { layoutForQuality } from './pdfExportQuality';
import { isJpeg, isPng, webpToPngBytes } from './pdfImageBytes';
import {
  renderHtmlEmailRasterFallback,
  renderHtmlEmailVector,
  type HtmlPdfCursor,
} from './renderEmailHtmlToPdf';

export type { PdfExportQuality, ImageFitMode } from './pdfExportQuality';

export interface PdfConversionOptions {
  includeEmailContent: boolean;
  showAttachmentList: boolean;
  embedAttachmentIds: string[];
  /** Typography / margins / raster density */
  quality?: PdfExportQuality;
  /** How inline / attachment images map into page space */
  imageFit?: ImageFitMode;
  /**
   * When true, uses high-DPI html2canvas for HTML body (worse text selectability).
   * Default false: vector text via DOM → pdf-lib.
   */
  useRasterHtmlFallback?: boolean;
  /** Page width in pt (default A4 portrait). */
  pageWidthPt?: number;
  /** Page height in pt (default A4 portrait). */
  pageHeightPt?: number;
  /** When false, skips From / To / Cc / Date block. Default true. */
  includeHeaders?: boolean;
}

export interface PdfCursor {
  doc: PDFDocument;
  page: PDFPage;
  y: number;
  fonts: EmbeddedBodyFonts;
  layout: ReturnType<typeof layoutForQuality>;
  metaBold: PDFFont;
  metaRegular: PDFFont;
  pageWidthPt: number;
  pageHeightPt: number;
}

function pageBottom(layout: ReturnType<typeof layoutForQuality>): number {
  return layout.margin + layout.footerReserved;
}

function freshPage(c: PdfCursor): void {
  c.page = c.doc.addPage([c.pageWidthPt, c.pageHeightPt]);
  c.y = c.pageHeightPt - c.layout.margin;
}

function ensureVertical(c: PdfCursor, need: number): void {
  if (c.y - need < pageBottom(c.layout)) {
    freshPage(c);
  }
}

function wrapWords(text: string, font: PDFFont, size: number, maxW: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const trial = line ? `${line} ${w}` : w;
    if (font.widthOfTextAtSize(trial, size) <= maxW) line = trial;
    else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines.length ? lines : [''];
}

function drawTextLines(c: PdfCursor, text: string, size: number, color: RGB, lineHeight: number): void {
  const maxW = c.pageWidthPt - 2 * c.layout.margin;
  const blocks = text.split(/\r?\n/);
  for (const block of blocks) {
    const trimmed = block.trimEnd();
    if (!trimmed) {
      c.y -= lineHeight * 0.45;
      continue;
    }
    for (const ln of wrapWords(trimmed, c.metaRegular, size, maxW)) {
      ensureVertical(c, lineHeight);
      c.page.drawText(ln, { x: c.layout.margin, y: c.y, size, font: c.metaRegular, color });
      c.y -= lineHeight;
    }
    c.y -= lineHeight * 0.2;
  }
}

function drawLabelValue(c: PdfCursor, label: string, value: string): void {
  const size = c.layout.metaSize;
  const lh = size * 1.35;
  const labelText = `${label}:`;
  ensureVertical(c, lh + 2);
  c.page.drawText(labelText, {
    x: c.layout.margin,
    y: c.y,
    size,
    font: c.metaBold,
    color: rgb(0.25, 0.25, 0.28),
  });
  c.y -= lh;
  drawTextLines(c, value || '—', size, rgb(0.35, 0.357, 0.38), lh);
  c.y -= 4;
}

function computeAttachImageSize(
  imgW: number,
  imgH: number,
  fit: ImageFitMode,
  maxW: number,
  maxH: number,
): { w: number; h: number } {
  if (fit === 'original') {
    const wPt = imgW * (72 / 96);
    const hPt = imgH * (72 / 96);
    if (wPt <= maxW && hPt <= maxH) return { w: wPt, h: hPt };
    const r = Math.min(maxW / wPt, maxH / hPt);
    return { w: wPt * r, h: hPt * r };
  }
  if (fit === 'fitWidth') {
    const r = Math.min(1, maxW / imgW);
    return { w: imgW * r, h: imgH * r };
  }
  const r = Math.min(maxW / imgW, maxH / imgH, 1);
  return { w: imgW * r, h: imgH * r };
}

async function embedAttachmentImage(
  content: Uint8Array,
  mime: string,
  doc: PDFDocument,
): Promise<Awaited<ReturnType<PDFDocument['embedPng']>>> {
  const lower = mime.toLowerCase();
  if (lower.includes('webp')) {
    return doc.embedPng(await webpToPngBytes(content));
  }
  if (isPng(content)) {
    return doc.embedPng(content);
  }
  if (isJpeg(content) || lower.includes('jpeg') || lower.includes('jpg')) {
    const blob = new Blob([new Uint8Array(content)], { type: 'image/jpeg' });
    const bmp = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    const c = document.createElement('canvas');
    c.width = bmp.width;
    c.height = bmp.height;
    const ctx = c.getContext('2d');
    if (!ctx) {
      bmp.close();
      return doc.embedJpg(content);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bmp, 0, 0);
    bmp.close();
    const jpegBlob = await new Promise<Blob>((res, rej) => {
      c.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), 'image/jpeg', 0.96);
    });
    return doc.embedJpg(new Uint8Array(await jpegBlob.arrayBuffer()));
  }
  try {
    return await doc.embedPng(content);
  } catch {
    return doc.embedJpg(content);
  }
}

async function embedBinaryAsPages(c: PdfCursor, att: NormalizedAttachment, imageFit: ImageFitMode): Promise<void> {
  const mime = att.mimeType.toLowerCase();
  const { content } = att;

  if (mime === 'application/pdf' || att.filename.toLowerCase().endsWith('.pdf')) {
    const src = await PDFDocument.load(content.slice());
    const copied = await c.doc.copyPages(src, src.getPageIndices());
    copied.forEach((p) => c.doc.addPage(p));
    const last = c.doc.getPageCount() - 1;
    c.page = c.doc.getPage(last);
    c.y = c.layout.margin + c.layout.footerReserved;
    return;
  }

  if (
    mime.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'webp'].some((e) => att.filename.toLowerCase().endsWith(`.${e}`))
  ) {
    const image = await embedAttachmentImage(content, mime, c.doc);

    const naturalW = image.width;
    const naturalH = image.height;
    const maxW = c.pageWidthPt - 2 * c.layout.margin;
    const maxH = c.y - pageBottom(c.layout);
    const { w, h } = computeAttachImageSize(naturalW, naturalH, imageFit, maxW, Math.max(80, maxH));

    ensureVertical(c, h + 16);
    if (c.y - h < pageBottom(c.layout)) {
      freshPage(c);
    }
    c.page.drawImage(image, { x: c.layout.margin, y: c.y - h, width: w, height: h });
    c.y -= h + 16;
    return;
  }

  if (
    mime === 'text/plain' ||
    mime === 'text/csv' ||
    mime === 'application/csv' ||
    att.filename.toLowerCase().endsWith('.txt') ||
    att.filename.toLowerCase().endsWith('.csv')
  ) {
    const decoder = new TextDecoder('utf-8', { fatal: false });
    const txt = decoder.decode(content);
    ensureVertical(c, 22);
    c.page.drawText(`Attachment: ${att.filename}`, {
      x: c.layout.margin,
      y: c.y,
      size: c.layout.sectionTitleSize,
      font: c.metaBold,
      color: rgb(0.2, 0.2, 0.22),
    });
    c.y -= 20;
    drawTextLines(c, txt, c.layout.bodySize * 0.92, rgb(0.15, 0.15, 0.18), c.layout.bodySize * 1.25);
  }
}

function drawFooter(page: PDFPage, index: number, total: number, font: PDFFont, margin: number): void {
  const label = `Page ${index + 1} of ${total}`;
  const size = 8.5;
  const w = font.widthOfTextAtSize(label, size);
  const pageW = page.getWidth();
  page.drawText(label, {
    x: pageW - margin - w,
    y: margin * 0.55,
    size,
    font,
    color: rgb(0.55, 0.55, 0.58),
  });
}

function applyFooters(doc: PDFDocument, font: PDFFont, margin: number): void {
  const pages = doc.getPages();
  const n = pages.length;
  pages.forEach((p, i) => drawFooter(p, i, n, font, margin));
}

function kbDecimalPlaces(size: number): number {
  return size >= 102400 ? 0 : 1;
}

export async function convertEmailToPdf(
  email: NormalizedEmail,
  options: PdfConversionOptions,
): Promise<Uint8Array> {
  const layout = layoutForQuality(options.quality);
  const imageFit: ImageFitMode = options.imageFit ?? 'fitPage';
  const pageWidthPt = options.pageWidthPt ?? 595.28;
  const pageHeightPt = options.pageHeightPt ?? 841.89;
  const includeHeaders = options.includeHeaders !== false;
  const doc = await PDFDocument.create();

  let fonts: EmbeddedBodyFonts;
  try {
    fonts = await embedBodyFonts(doc);
  } catch {
    fonts = await embedStandardFallbackFonts(doc);
  }

  let c: PdfCursor = {
    doc,
    page: doc.addPage([pageWidthPt, pageHeightPt]),
    y: pageHeightPt - layout.margin,
    fonts,
    layout,
    metaBold: fonts.bold,
    metaRegular: fonts.regular,
    pageWidthPt,
    pageHeightPt,
  };

  const subjLine = layout.subjectSize * 1.15;
  const subjectLines = wrapWords(email.subject, fonts.bold, layout.subjectSize, pageWidthPt - 2 * layout.margin);
  for (const ln of subjectLines) {
    ensureVertical(c, subjLine + 2);
    c.page.drawText(ln, {
      x: layout.margin,
      y: c.y,
      size: layout.subjectSize,
      font: fonts.bold,
      color: rgb(0.08, 0.09, 0.12),
    });
    c.y -= subjLine + 2;
  }
  c.y -= 6;

  if (includeHeaders) {
    ensureVertical(c, 22);
    c.page.drawText('Message details', {
      x: layout.margin,
      y: c.y,
      size: layout.sectionTitleSize,
      font: fonts.bold,
      color: rgb(0.35, 0.37, 0.42),
    });
    c.y -= 18;

    drawLabelValue(c, 'From', email.from);
    drawLabelValue(c, 'To', email.to);
    drawLabelValue(c, 'Cc', email.cc);
    drawLabelValue(c, 'Date', email.date);

    ensureVertical(c, 16);
    c.page.drawLine({
      start: { x: layout.margin, y: c.y },
      end: { x: pageWidthPt - layout.margin, y: c.y },
      thickness: 0.65,
      color: rgb(0.88, 0.89, 0.92),
    });
    c.y -= 22;
  }

  if (options.includeEmailContent) {
    if (email.htmlBody) {
      const withCid = await resolveCidInHtml(email.htmlBody, email.attachments);
      const safe = sanitizeEmailHtml(withCid);
      ensureVertical(c, 24);
      c.page.drawText('Message body', {
        x: layout.margin,
        y: c.y,
        size: layout.sectionTitleSize,
        font: fonts.bold,
        color: rgb(0.35, 0.37, 0.42),
      });
      c.y -= layout.bodySize * 1.6;

      const htmlC: HtmlPdfCursor = {
        doc: c.doc,
        page: c.page,
        y: c.y,
        fonts: c.fonts,
        layout: c.layout,
        pageWidthPt: c.pageWidthPt,
        pageHeightPt: c.pageHeightPt,
      };

      if (options.useRasterHtmlFallback) {
        await renderHtmlEmailRasterFallback(htmlC, safe);
      } else {
        await renderHtmlEmailVector(htmlC, safe, imageFit, async () => {
          await new Promise<void>((r) => {
            window.requestAnimationFrame(() => r());
          });
        });
      }
      c.page = htmlC.page;
      c.y = htmlC.y;
    } else if (email.textBody) {
      ensureVertical(c, 22);
      c.page.drawText('Message body', {
        x: layout.margin,
        y: c.y,
        size: layout.sectionTitleSize,
        font: fonts.bold,
        color: rgb(0.35, 0.37, 0.42),
      });
      c.y -= 18;
      drawTextLines(c, email.textBody, layout.bodySize, rgb(0.12, 0.13, 0.16), layout.bodySize * layout.lineHeightMult);
    } else {
      drawTextLines(
        c,
        '(No body content)',
        layout.bodySize,
        rgb(0.5, 0.5, 0.52),
        layout.bodySize * layout.lineHeightMult,
      );
    }
  }

  if (options.showAttachmentList && email.attachments.length > 0) {
    ensureVertical(c, 32);
    c.page.drawText('Attachments', {
      x: layout.margin,
      y: c.y,
      size: layout.sectionTitleSize + 0.5,
      font: fonts.bold,
      color: rgb(0.35, 0.37, 0.42),
    });
    c.y -= 18;

    for (const att of email.attachments) {
      const kb = (att.size / 1024).toFixed(kbDecimalPlaces(att.size));
      const flag = att.embeddableInPdf ? 'Can embed in PDF' : (att.unsupportedReason || 'Not embedded');
      const row = `• ${att.filename}  ·  ${att.mimeType}  ·  ${kb} KB  ·  ${flag}`;
      drawTextLines(c, row, layout.metaSize, rgb(0.28, 0.3, 0.34), layout.metaSize * 1.32);
    }
  }

  const embedSet = new Set(options.embedAttachmentIds);
  const toEmbed = email.attachments.filter((a) => embedSet.has(a.id) && a.embeddableInPdf);

  for (const att of toEmbed) {
    ensureVertical(c, 28);
    c.page.drawText(`Embedded: ${att.filename}`, {
      x: layout.margin,
      y: c.y,
      size: layout.sectionTitleSize,
      font: fonts.bold,
      color: rgb(0.35, 0.37, 0.42),
    });
    c.y -= 18;
    await embedBinaryAsPages(c, att, imageFit);
  }

  applyFooters(doc, fonts.regular, layout.margin);
  return doc.save();
}

export function triggerPdfDownload(bytes: Uint8Array, fileName: string): void {
  const blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const safe = fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`;
  link.download = safe;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 15_000);
}
